<template>
  <div ref="root" class="ds-combobox">
    <label v-if="label" class="ds-combobox__label" :for="id">{{ label }}</label>
    <input
      :id="id"
      :value="query ?? ''"
      class="ds-field ds-combobox__input"
      :class="{ 'ds-combobox__input--picked': !!selectedDealerLabel }"
      type="text"
      autocomplete="off"
      :placeholder="selectedDealerLabel || placeholder"
      @input="onInput"
      @focus="openList"
    />
    <ul v-if="listOpen && dealerOptions.length" class="ds-combobox__list" :style="listStyle">
      <li
        v-for="dealer in filteredDealers"
        :key="dealer.DealerId"
        class="ds-combobox__item"
        @click="selectDealer(dealer)"
      >
        <span class="ds-combobox__id">{{ dealer.DealerId }}</span>
        <span class="ds-combobox__name">{{ dealer.Name }}</span>
      </li>
      <li v-if="!filteredDealers.length" class="ds-combobox__empty">Geen dealers gevonden</li>
    </ul>
  </div>
</template>

<script>
import PurchaseOrder from '@/api/server/PurchaseOrder'

const MAX_VISIBLE_DEALERS = 50

export default {
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    id: {
      type: String,
      default: 'dealer-combobox',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Zoek op dealernummer of naam...',
    },
  },
  emits: ['update:modelValue', 'select'],
  data() {
    return {
      establishments: [],
      listOpen: false,
      // null means "not typing", so the full list stays visible after a selection
      query: null,
      listPosition: {
        top: 0,
        bottom: 0,
        left: 0,
        minWidth: 0,
        maxHeight: 360,
        openUpward: false,
      },
    }
  },
  computed: {
    dealerOptions() {
      const byDealerId = new Map()
      this.establishments
        .filter((establishment) => establishment.DealerId)
        .forEach((establishment) => {
          if (!byDealerId.has(establishment.DealerId)) {
            byDealerId.set(establishment.DealerId, {
              DealerId: establishment.DealerId,
              Name: establishment.Name,
            })
          }
        })
      return [...byDealerId.values()]
    },
    filteredDealers() {
      const query = String(this.query ?? '')
        .toLowerCase()
        .trim()
      const list = !query
        ? this.dealerOptions
        : this.dealerOptions.filter(
            (dealer) =>
              String(dealer.DealerId).toLowerCase().includes(query) ||
              (dealer.Name || '').toLowerCase().includes(query)
          )
      return list.slice(0, MAX_VISIBLE_DEALERS)
    },
    selectedDealerLabel() {
      if (this.modelValue === null || this.modelValue === '') return ''
      const selected = this.dealerOptions.find(
        (dealer) => String(dealer.DealerId) === String(this.modelValue)
      )
      return selected ? `${selected.DealerId} - ${selected.Name}` : String(this.modelValue)
    },
    listStyle() {
      const style = {
        left: `${this.listPosition.left}px`,
        minWidth: `${this.listPosition.minWidth}px`,
        maxHeight: `${this.listPosition.maxHeight}px`,
      }
      if (this.listPosition.openUpward) {
        style.bottom = `${this.listPosition.bottom}px`
      } else {
        style.top = `${this.listPosition.top}px`
      }
      return style
    },
  },
  async mounted() {
    document.addEventListener('click', this.onDocumentClick)
    await this.loadEstablishments()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    async loadEstablishments() {
      try {
        const { data } = await PurchaseOrder.getEstablishments()
        this.establishments = data || []
      } catch {
        this.establishments = []
      }
    },
    openList(event) {
      this.query = null
      const rect = event.target.getBoundingClientRect()
      const gap = 6
      const preferredHeight = 360
      const minHeight = 120
      const spaceBelow = window.innerHeight - rect.bottom - gap
      const spaceAbove = rect.top - gap
      const openUpward = spaceBelow < preferredHeight && spaceAbove > spaceBelow

      this.listPosition = {
        left: rect.left,
        minWidth: rect.width,
        openUpward,
        top: rect.bottom + gap,
        bottom: window.innerHeight - rect.top + gap,
        maxHeight: Math.max(openUpward ? spaceAbove : spaceBelow, minHeight),
      }
      this.listOpen = true
    },
    onInput(event) {
      this.query = event.target.value
      if (this.modelValue !== null && this.modelValue !== '') {
        this.$emit('update:modelValue', '')
      }
    },
    selectDealer(dealer) {
      this.listOpen = false
      this.query = null
      this.$emit('update:modelValue', dealer.DealerId)
      this.$emit('select', dealer)
    },
    onDocumentClick(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.listOpen = false
        this.query = null
      }
    },
  },
}
</script>

<!-- No styles of its own: presentation comes from the design system
     (styles/ds/_combobox.scss and _field.scss), which is emitted globally so
     this shared component looks the same on migrated and legacy pages. -->
